import { useState, useEffect } from 'react'
import { useAccessibility } from '../../../context/AccessibilityContext/AccessibilityContext'
import Block from "../../../components/Block/Block"
import Button from "../../../components/Button/Button"
import FadeIn from "../../../components/FadeIn/FadeIn"
import { 
    FaMapMarkerAlt, 
    FaPhone, 
    FaEnvelope, 
    FaWifi,
    FaParking,
    FaShower,
    FaShoppingBag,
    FaClock,
    FaChevronDown,
    FaChevronUp,
    FaRoute
} from 'react-icons/fa'
import { MdOutlineLocalParking } from 'react-icons/md'
import sambologo from '../../../assets/sambologo.png'
import style from './addressblock.module.scss'

const AddressBlock = ({ onShowOnMap, branches, loading }) => {
    const [isMobile, setIsMobile] = useState(false)
    const [expandedBranch, setExpandedBranch] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [filteredBranches, setFilteredBranches] = useState([])
    const { settings: { contrast } } = useAccessibility()

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 992)
        }

        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        
        return () => {
            window.removeEventListener('resize', checkScreenSize)
        }
    }, [])

    useEffect(() => {
        if (branches) {
            const filtered = branches.filter(branch => {
                const title = branch.attributes.Title?.toLowerCase() || ''
                const address = branch.attributes.Address?.toLowerCase() || ''
                const query = searchQuery.toLowerCase()
                return title.includes(query) || address.includes(query)
            })
            setFilteredBranches(filtered)
        }
    }, [branches, searchQuery])

    const getBranchCoordinates = (branch) => {
        if (branch.attributes.coordinates) {
            return branch.attributes.coordinates;
        }
        if (branch.attributes.MapY && branch.attributes.MapX) {
            return [branch.attributes.MapY, branch.attributes.MapX];
        }
        return null;
    };

    const handleShowOnMapClick = (branch) => {
        const coordinates = getBranchCoordinates(branch);
        if (coordinates && onShowOnMap) {
            onShowOnMap(coordinates);
        }
    };

    const toggleBranch = (id) => {
        setExpandedBranch(expandedBranch === id ? null : id)
    }

    const buildYandexRoute = (branch) => {
        const coords = getBranchCoordinates(branch)
        if (coords) {
            const [lat, lon] = coords
            window.open(`https://yandex.ru/maps/?rtext=~${lat},${lon}&rtt=auto`, '_blank')
        }
    }

    const getBranchFeatures = (branch) => {
        const features = [];
        
        if (branch.attributes.Parking) features.push('Парковка');
        if (branch.attributes.Shower) features.push('Душевые');
        if (branch.attributes.Wifi) features.push('Wi-Fi');
        if (branch.attributes.Shop) features.push('Магазин');
        if (branch.attributes['24/7']) features.push('Круглосуточный');
        
        return features;
    }

    return (
        <Block
            flexDirection='column'
            justifyContent='center'
            alignItems='start'
            width={isMobile ? '100%' : '75%'}
            height='fit-content'
            background={contrast !== 'default' ? 'var(--bg-primary)' : 'rgba(255, 255, 255, 0.92)'}
            className={style.blockWrapper}
        >
            <FadeIn>
                <div className={style.content}>
                    {loading ? (
                        <div className={style.loadingContainer}>
                            <div className={style.loadingSpinner}></div>
                            <div className={style.loadingContent}>
                                <span className={style.loadingTitle}>Загрузка филиалов</span>
                                <span className={style.loadingSubtitle}>Пожалуйста, подождите...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={style.searchSection}>
                                <div className={style.searchWrapper}>
                                    <FaMapMarkerAlt className={style.searchIcon} />
                                    <input
                                        type="text"
                                        placeholder="Поиск зала по названию или адресу..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={style.searchInput}
                                    />
                                    {searchQuery && (
                                        <button 
                                            className={style.clearSearch}
                                            onClick={() => setSearchQuery('')}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className={style.branchesGrid}>
                                {filteredBranches.length === 0 ? (
                                    <div className={style.emptyState}>
                                        <div className={style.emptyIcon}>🔍</div>
                                        <h3>Ничего не найдено</h3>
                                        <p>Попробуйте изменить параметры поиска</p>
                                    </div>
                                ) : (
                                    filteredBranches.map((branch, index) => {
                                        const hasCoordinates = getBranchCoordinates(branch) !== null;
                                        const isExpanded = expandedBranch === branch.id;
                                        const features = getBranchFeatures(branch);
                                        
                                        return (
                                            <div 
                                                key={branch.id} 
                                                className={`${style.branchCard} ${isExpanded ? style.expanded : ''}`}
                                                style={{
                                                    '--card-delay': `${index * 0.05}s`
                                                }}
                                            >
                                                <div className={style.cardHeader} onClick={() => toggleBranch(branch.id)}>
                                                    <div className={style.headerLeft}>
                                                        <div className={style.branchIcon}>
                                                            <img src={sambologo} alt="Самбо" className={style.branchLogo} />
                                                        </div>
                                                        <div className={style.branchInfo}>
                                                            <h3 className={style.branchTitle}>
                                                                {branch.attributes.Title}
                                                            </h3>
                                                            <div className={style.branchAddress}>
                                                                <FaMapMarkerAlt className={style.iconSmall} />
                                                                {branch.attributes.Address}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={style.headerRight}>
                                                        <div className={style.expandIcon}>
                                                            {isExpanded ? (
                                                                <FaChevronUp className={style.chevron} />
                                                            ) : (
                                                                <FaChevronDown className={style.chevron} />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className={`${style.cardContent} ${isExpanded ? style.show : ''}`}>
                                                    <div className={style.contentInner}>
                                                        <div className={style.contactGrid}>
                                                            <div className={style.contactItem}>
                                                                <div className={style.contactIconWrapper}>
                                                                    <FaPhone className={style.contactIcon} />
                                                                </div>
                                                                <div className={style.contactInfo}>
                                                                    <span className={style.contactLabel}>Телефон</span>
                                                                    <a href={`tel:${branch.attributes.Phone}`} className={style.contactValue}>
                                                                        {branch.attributes.Phone}
                                                                    </a>
                                                                </div>
                                                            </div>
                                                            
                                                            {branch.attributes.Email && (
                                                                <div className={style.contactItem}>
                                                                    <div className={style.contactIconWrapper}>
                                                                        <FaEnvelope className={style.contactIcon} />
                                                                    </div>
                                                                    <div className={style.contactInfo}>
                                                                        <span className={style.contactLabel}>Email</span>
                                                                        <a href={`mailto:${branch.attributes.Email}`} className={style.contactValue}>
                                                                            {branch.attributes.Email}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className={style.featuresGrid}>
                                                            <div className={style.featuresList}>
                                                                {features.map((feature, i) => {
                                                                    let icon;
                                                                    if (feature.includes('парковка') || feature.includes('Парковка')) icon = <MdOutlineLocalParking />;
                                                                    else if (feature.includes('Душевые')) icon = <FaShower />;
                                                                    else if (feature.includes('Wi-Fi')) icon = <FaWifi />;
                                                                    else if (feature.includes('Магазин')) icon = <FaShoppingBag />;
                                                                    else if (feature.includes('Круглосуточный')) icon = <FaClock />;
                                                                    else icon = <FaParking />;
                                                                    return (
                                                                        <div key={i} className={style.featureItem}>
                                                                            <span className={style.featureIcon}>{icon}</span>
                                                                            <span className={style.featureText}>{feature}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        <div className={style.cardActions}>
                                                            <Button
                                                                width={isMobile ? '100%' : 'auto'}
                                                                height='52px'
                                                                text={
                                                                    <span className={style.buttonContent}>
                                                                        <FaRoute />
                                                                        Построить маршрут на Яндекс.Картах
                                                                    </span>
                                                                }
                                                                color='#ffffff'
                                                                fontFamily='Roboto, sans-serif'
                                                                fontSize='0.95rem'
                                                                fontWeight='600'
                                                                border='none'
                                                                background='linear-gradient(135deg, #e52d27 0%, #b31217 100%)'
                                                                onClick={() => buildYandexRoute(branch)}
                                                                disabled={!hasCoordinates}
                                                                className={style.routeButton}
                                                            />
                                                            
                                                            <button 
                                                                className={style.mapButton}
                                                                onClick={() => handleShowOnMapClick(branch)}
                                                                disabled={!hasCoordinates}
                                                            >
                                                                <FaMapMarkerAlt />
                                                                <span>Показать на карте</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </div>
            </FadeIn>
        </Block>
    )
}

export default AddressBlock